import { useCallback, useMemo, useState } from 'react';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import {
  ColumnSort,
  createColumnHelper,
  filterFns,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Checkbox,
  LinkButton,
  Scrollbar,
  Stack,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHeader,
  Text,
  useColorMode,
  useColorStyle,
  Flex,
  Spinner,
} from '@tonic-one/react';
import { DeleteIcon, FileCopyOIcon } from '@tonic-one/react-icons';
import { dataAttr } from '@tonic-one/utils';
import {
  CorrelatedExpression,
  CorrelatedExpressionShallow,
  RuleType,
  RuleTypeFilter,
} from '../types';
import IconButton from '@Components/IconButton';
import { useTranslation } from 'react-i18next';
import PreDefinedExpressionModal from './PreDefinedExpressionModal';
import CustomExpressionModal from './CustomExpressionModal';
import DeleteExpressionModal from './DeleteExpressionModal';

const ROW_HEIGHT = 40;

const CorrelatedExpressionsTable = (props: {
  expressions: CorrelatedExpressionShallow[];
  nameFilter: string;
  expressionTypeFilter: RuleTypeFilter;
  loading?: boolean;
  onCopyExpression: (expression: CorrelatedExpression) => Promise<void>;
  onEditExpression: (expression: CorrelatedExpression) => Promise<void>;
  onDeleteExpression: (expressionId: string) => Promise<void>;
}) => {
  const {
    expressions,
    nameFilter,
    expressionTypeFilter,
    loading,
    onCopyExpression,
    onEditExpression,
    onDeleteExpression,
  } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<ColumnSort[]>([
    { id: 'raiExpBuiltIn', desc: false },
    { id: 'raiExpName', desc: false },
  ]);

  const [expressionToView, setExpressionToView] =
    useState<CorrelatedExpressionShallow | null>(null);
  const [customExpressionToView, setCustomExpressionToView] =
    useState<CorrelatedExpressionShallow | null>(null);
  const [customExpressionToDelete, setCustomExpressionToDelete] =
    useState<CorrelatedExpressionShallow | null>(null);
  const [expressionToCopy, setExpressionToCopy] =
    useState<CorrelatedExpressionShallow | null>(null);

  const handleCopyExpression = useCallback(
    async (expressionToCopy: CorrelatedExpressionShallow) => {
      setExpressionToCopy({
        ...expressionToCopy,
        raiExpName: `${expressionToCopy.raiExpName} (${t(
          'common.action.label.copy'
        )})`,
        raiExpBuiltIn: RuleType.UserDefined,
      });
    },
    [t]
  );

  const columnFilters = useMemo(
    () => [
      { id: 'raiExpName', value: nameFilter },
      { id: 'raiExpBuiltIn', value: expressionTypeFilter },
    ],
    [nameFilter, expressionTypeFilter]
  );

  const columns = useMemo(() => {
    const colHelper = createColumnHelper<CorrelatedExpressionShallow>();
    return [
      colHelper.display({
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
        meta: {
          customColumnStyle: {
            px: '4x',
          },
        },
      }),
      colHelper.accessor('raiExpName', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedExpressions.table.filterName')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <LinkButton
            overflow="hidden"
            onClick={() => {
              if (cell.row.original.raiExpBuiltIn === RuleType.PreDefined) {
                setExpressionToView(cell.row.original);
              } else {
                setCustomExpressionToView(cell.row.original);
              }
            }}
          >
            <TextEllipsis popperProps={{ wordBreak: 'break-word' }}>
              {cell.getValue()}
            </TextEllipsis>
          </LinkButton>
        ),
        filterFn: (row, columnId, value, addMeta) => {
          if (value === '') return true;
          return filterFns.includesString(row, columnId, value, addMeta);
        },
        minSize: 295,
      }),
      colHelper.accessor('raiExpDesc', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedExpressions.table.description')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <TextEllipsis popperProps={{ wordBreak: 'break-word' }}>
            {cell.getValue()}
          </TextEllipsis>
        ),
        minSize: 660,
      }),
      colHelper.accessor('raiExpBuiltIn', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedExpressions.table.filterType')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <TextEllipsis>
            {cell.getValue() === 0
              ? t('globalSetting.correlatedRules.userDefined')
              : t('globalSetting.correlatedRules.preDefined')}
          </TextEllipsis>
        ),
        filterFn: (row, _, value) => {
          if (value === RuleTypeFilter.All) return true;
          else if (value === RuleTypeFilter.PreDefined) {
            return row.original.raiExpBuiltIn === 1;
          }
          return row.original.raiExpBuiltIn === 0;
        },
        minSize: 160,
        meta: {
          customColumnStyle: {
            flex: 'auto',
            overflow: 'hidden',
          },
        },
      }),
      colHelper.display({
        id: 'actions',
        header: () => null,
        cell: (cell) => {
          const builtIn = cell.row.original.raiExpBuiltIn;
          const isEmailLoop = cell.row.original.raiExpMarkers === 'email_loop';
          return builtIn ? null : (
            <Stack gap="2x" flexDirection="row">
              <IconButton
                tooltipLabel={t('common.action.label.copy')}
                onClick={() => handleCopyExpression(cell.row.original)}
                disabled={isEmailLoop}
              >
                <FileCopyOIcon />
              </IconButton>

              {!isEmailLoop && (
                <IconButton
                  tooltipLabel={t('common.action.label.delete')}
                  onClick={() => setCustomExpressionToDelete(cell.row.original)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          );
        },
        size: 90,
      }),
    ];
  }, [handleCopyExpression, t]);

  const table = useReactTable({
    data: expressions || [],
    columns,
    defaultColumn: {
      minSize: 48,
    },
    state: {
      rowSelection,
      columnFilters,
      sorting,
    },
    enableRowSelection: true,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      if (typeof updater === 'function') {
        setSorting(updater(sorting));
      }
    },
    getRowId: (originalRow) => {
      return originalRow.raiExpId;
    },
  });

  return (
    <>
      <Scrollbar
        flex="auto"
        width="100%"
        height="100%"
        overflowX="auto"
        overflowY="hidden"
      >
        <Table layout="flexbox" minWidth="100%" height="100%">
          <TableHeader position="sticky" top={0} zIndex={1}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow width="100%" key={`header-group-${headerGroup.id}`}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={`header-${header.id}`}
                    width={header.getSize()}
                    minWidth={header.column.columnDef.minSize}
                    {...header.column.columnDef.meta?.customColumnStyle}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {loading && (
            <Flex flex={1} alignItems="center" justifyContent="center">
              <Spinner />
            </Flex>
          )}
          {!loading && (
            <Scrollbar
              display="flex"
              flexDirection="column"
              flex="1 1 auto"
              overflowY="auto"
              height="100%"
            >
              <TableBody height="100%" width="100%">
                {table.getRowModel().rows.length === 0 && !loading && (
                  <Flex
                    width="100%"
                    height={ROW_HEIGHT * 2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Text color={colorStyle.color.secondary}>
                      {t('common.noData')}
                    </Text>
                  </Flex>
                )}
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    minWidth="100%"
                    height={ROW_HEIGHT}
                    key={`row-${row.id}`}
                    data-selected={dataAttr(row.getIsSelected())}
                    _hover={{
                      backgroundColor: colorStyle.background.selected,
                    }}
                    _selected={{
                      backgroundColor: colorStyle.background.selected,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={`cell-${cell.id}`}
                        display="flex"
                        alignItems="center"
                        width={cell.column.getSize()}
                        minWidth={cell.column.columnDef.minSize}
                        {...cell.column.columnDef.meta?.customColumnStyle}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Scrollbar>
          )}
        </Table>
      </Scrollbar>
      {expressionToView && (
        <PreDefinedExpressionModal
          expression={expressionToView}
          isOpen={expressionToView.raiExpBuiltIn === 1}
          onClose={() => setExpressionToView(null)}
        />
      )}
      {customExpressionToView && (
        <CustomExpressionModal
          defaultExpression={customExpressionToView}
          isOpen={!!customExpressionToView}
          onSave={async (expression) => {
            try {
              await onEditExpression(expression);
              setCustomExpressionToView(null);
            } catch (e) {
              // User has to modify the expression name or retry
              console.error(e);
            }
          }}
          onClose={() => {
            setCustomExpressionToView(null);
          }}
        />
      )}
      {expressionToCopy && (
        <CustomExpressionModal
          defaultExpression={expressionToCopy}
          isOpen={!!expressionToCopy}
          onSave={async (expression) => {
            try {
              await onCopyExpression({ ...expression, raiExpId: '' });
              setExpressionToCopy(null);
            } catch (e) {
              // User has to modify the expression name or retry
              console.error(e);
            }
          }}
          onClose={() => {
            setExpressionToCopy(null);
          }}
        />
      )}
      {customExpressionToDelete && (
        <DeleteExpressionModal
          expression={customExpressionToDelete}
          isOpen={!!customExpressionToDelete}
          onConfirm={async () => {
            await onDeleteExpression(customExpressionToDelete.raiExpId);
            setCustomExpressionToDelete(null);
          }}
          onClose={() => setCustomExpressionToDelete(null)}
        />
      )}
    </>
  );
};

export default CorrelatedExpressionsTable;
